import React, {
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Checkbox, ToggleSwitch } from '@thd-olt-component-react/checkbox';
import { StoreSelector } from '../StoreSelector/StoreSelector';
import { ProductResultsContext } from '../ProductResultsContext';
import '../../styles/refinement.scss';

const OnDisplayRefinement = ({
  checkboxClassName,
  id,
  refinement,
  enableMultiStore,
  toggleSwitch,
  onMultiStoreSelectionChange,
  multiStoreSelection,
  onDisplayData
}) => {

  const { onDisplay, onDisplayChange } = onDisplayData;
  const { data } = useContext(ProductResultsContext);
  const onDisplaySelected = onDisplay === 'true';

  const [multiStoreIds, setMultiStoreIds] = useState(multiStoreSelection ? [multiStoreSelection] : []);

  const handleOnChange = () => {
    if (data && onDisplayChange && onDisplay !== 'true') {
      onDisplayChange({ useStoreFilter: 'true' });
    } else if (onDisplayChange && onDisplay === 'true') {
      onDisplayChange({ useStoreFilter: 'false' });
    }
  };

  const multiStoreCallback = useCallback(({ stores }) => {
    const idCount = multiStoreIds?.length;
    const newMultiStoreIds = stores.filter((store) => store.checked)
      .map((checkedStore) => parseInt(checkedStore.storeId, 10));
    if (newMultiStoreIds?.length < idCount && !onDisplaySelected) {
      const copiedRefinement = { ...refinement };
      copiedRefinement.url = `${copiedRefinement.url}&storeSelection=${newMultiStoreIds.join()}`;
      onDisplayChange({ useStoreFilter: 'true' });
    }
    setMultiStoreIds(newMultiStoreIds);
  });

  useEffect(() => {
    if (onDisplaySelected && enableMultiStore && onMultiStoreSelectionChange) {
      onMultiStoreSelectionChange({ multiStoreIds });
    }
  }, [multiStoreIds]);

  const generateRefinementLabel = () => {
    return onDisplayData.itemCount > 0
      ? `${onDisplayData.itemCount} items on display at Store Today `
      : 'On Display at Store Today';
  };

  const labelClassName = classNames('refinement__link', {
    'refinement__link--special-value': refinement.label === 'Special Values'
  });

  return (
    <>
      {!toggleSwitch && (
        <Checkbox
          className={checkboxClassName}
          disabled={parseInt(onDisplayData.itemCount, 10) === 0}
          id={id}
          key={id}
          data-refinement="On Display"
          data-refinementvalue="On Display"
          data-active="yes"
          data-omni="true"
          onChange={handleOnChange}
          checked={!!onDisplaySelected}
          value={refinement.refinementKey || ''}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <h3 className={labelClassName} onClick={handleOnChange}>{generateRefinementLabel()}</h3>
        </Checkbox>
      )}
      {toggleSwitch && (
        <ToggleSwitch
          className="refinement__toggle-switch"
          id={id}
          key={id}
          onChange={handleOnChange}
          checked={!!onDisplaySelected}
        >
          <div className={labelClassName}>In Stock Only</div>
        </ToggleSwitch>
      )}
      {refinement.label.toUpperCase() === 'PICK UP TODAY' && (
        <StoreSelector multiStoreCallback={multiStoreCallback} multiStoreSelection={multiStoreSelection} />
      )}
    </>
  );
};

OnDisplayRefinement.propTypes = {
  checkboxClassName: PropTypes.string,
  dimension: PropTypes.shape({}),
  id: PropTypes.string,
  enableMultiStore: PropTypes.bool,
  multiStoreSelection: PropTypes.string,
  onMultiStoreSelectionChange: PropTypes.func,
  refinement: PropTypes.shape({
    label: PropTypes.string,
    recordCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refinementKey: PropTypes.string,
    selected: PropTypes.bool,
    url: PropTypes.string,
  }),
  onDisplayData: PropTypes.shape({
    onDisplay: PropTypes.string,
    onDisplayChange: PropTypes.func,
    itemCount: PropTypes.string,
    isStoreDisplay: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]),
  }),
  toggleSwitch: PropTypes.bool,
};

OnDisplayRefinement.defaultProps = {
  checkboxClassName: null,
  dimension: null,
  id: null,
  enableMultiStore: false,
  multiStoreSelection: null,
  onMultiStoreSelectionChange: null,
  refinement: null,
  onDisplayData: {
    onDisplay: 'false',
    onDisplayChange: null,
    itemCount: null,
    isStoreDisplay: 'false',
  },
  toggleSwitch: false,
};

OnDisplayRefinement.displayName = 'OnDisplayRefinement';

export { OnDisplayRefinement };
